import React, { useCallback, useEffect, useState } from 'react';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/LeadDashboard.css';

const LEAD_DASHBOARD_QUERY = `
  query GetLeadDashboard {
    getLeadDashboard {
      totalLeads
      newLeads
      qualifiedLeads
      convertedLeads
      lostLeads
      conversionRate
      revenuePotential
      byStatus { status count }
      byStage { stageId stageName count }
      monthlyTrend { month total qualified converted }
      bySource { source count }
    }
  }
`;

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '$0';
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

function statusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approve' || s === 'approved') return 'Qualified';
  if (s === 'assigned') return 'Converted';
  if (s === 'cancel') return 'Lost';
  return 'New';
}

export default function LeadDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const res = await graphqlRequest(LEAD_DASHBOARD_QUERY);
      setData(res?.getLeadDashboard || null);
    } catch (err) {
      console.error('Lead dashboard error:', err);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const refresh = () => fetchDashboard(true);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('lead:changed', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => {
      window.removeEventListener('lead:changed', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [fetchDashboard]);

  if (loading) {
    return <p className="lm-loading">Loading lead dashboard…</p>;
  }

  if (!data) {
    return <p className="lm-loading">No dashboard data available.</p>;
  }

  const maxTrend = Math.max(
    ...(data.monthlyTrend || []).map((m) => m.total),
    1,
  );
  const maxFunnel = Math.max(
    ...(data.byStage || []).map((s) => s.count),
    1,
  );

  return (
    <div className="lm-dashboard">
      <p className="lm-subtitle">
        {data.totalLeads} total leads · {data.qualifiedLeads} qualified ·{' '}
        {data.conversionRate}% conversion rate
      </p>

      <div className="lm-stats-grid">
        {[
          { label: 'Total Leads', value: data.totalLeads, tone: 'purple' },
          { label: 'New Leads (30d)', value: data.newLeads, tone: 'blue' },
          { label: 'Qualified', value: data.qualifiedLeads, tone: 'amber' },
          { label: 'Converted', value: data.convertedLeads, tone: 'green' },
          { label: 'Lost', value: data.lostLeads, tone: 'red' },
          { label: 'Conversion Rate', value: `${data.conversionRate}%`, tone: 'indigo' },
          { label: 'Revenue Potential', value: formatMoney(data.revenuePotential), tone: 'violet' },
        ].map((card) => (
          <div key={card.label} className={`lm-stat-card lm-stat-card--${card.tone}`}>
            <span className="lm-stat-label">{card.label}</span>
            <strong className="lm-stat-value">{card.value}</strong>
          </div>
        ))}
      </div>

      <div className="lm-panels">
        <div className="lm-panel">
          <h3>Monthly lead trend</h3>
          <div className="lm-trend-chart">
            {(data.monthlyTrend || []).map((m) => (
              <div key={m.month} className="lm-trend-col">
                <div className="lm-trend-bars">
                  <div
                    className="lm-bar lm-bar--total"
                    style={{ height: `${(m.total / maxTrend) * 100}%` }}
                    title={`Total: ${m.total}`}
                  />
                  <div
                    className="lm-bar lm-bar--qualified"
                    style={{ height: `${(m.qualified / maxTrend) * 100}%` }}
                    title={`Qualified: ${m.qualified}`}
                  />
                  <div
                    className="lm-bar lm-bar--converted"
                    style={{ height: `${(m.converted / maxTrend) * 100}%` }}
                    title={`Converted: ${m.converted}`}
                  />
                </div>
                <span className="lm-trend-month">{m.month}</span>
                <span className="lm-trend-count">{m.total}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lm-panel">
          <h3>Pipeline funnel</h3>
          <div className="lm-funnel">
            {(data.byStage || []).map((stage) => (
              <div key={stage.stageId || stage.stageName} className="lm-funnel-row">
                <span className="lm-funnel-label">{stage.stageName}</span>
                <div className="lm-funnel-track">
                  <div
                    className="lm-funnel-fill"
                    style={{ width: `${(stage.count / maxFunnel) * 100}%` }}
                  />
                </div>
                <span className="lm-funnel-count">{stage.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lm-panel lm-panel--full">
        <h3>Lead source performance</h3>
        <table className="lm-source-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Total leads</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {(data.bySource || []).map((row) => (
              <tr key={row.source}>
                <td>{row.source}</td>
                <td>{row.count}</td>
                <td>
                  {data.totalLeads
                    ? `${Math.round((row.count / data.totalLeads) * 100)}%`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lm-panel lm-panel--full">
        <h3>Status breakdown</h3>
        <div className="lm-status-chips">
          {(data.byStatus || []).map((row) => (
            <span key={row.status} className="lm-status-chip">
              {statusLabel(row.status)}: <strong>{row.count}</strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
